// frontend/src/pages/PaymentReturn.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Icon,
  Progress
} from '@chakra-ui/react';
import { FaCheckCircle, FaExclamationTriangle, FaClock } from 'react-icons/fa';

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, pending
  const [message, setMessage] = useState('Vérification de votre paiement...');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const checkoutId = searchParams.get('checkout_id');
    const urlStatus = searchParams.get('status');

    if (checkoutId) {
      verifyPayment(checkoutId);
    } else {
      // Vérifier s'il y a un paiement en cours dans le sessionStorage
      const pendingPayment = sessionStorage.getItem('pending_sumup_payment');
      if (pendingPayment) {
        try {
          const payment = JSON.parse(pendingPayment);
          if (payment.checkout_id) {
            verifyPayment(payment.checkout_id);
          } else {
            setStatus('error');
            setMessage('Informations de paiement manquantes');
          }
        } catch (error) {
          setStatus('error');
          setMessage('Erreur lors de la récupération des informations de paiement');
        }
      } else {
        setStatus('error');
        setMessage('Aucune information de paiement trouvée');
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      // Nettoyer le sessionStorage
      sessionStorage.removeItem('pending_sumup_payment');
      navigate('/contributions');
    }
  }, [status, countdown, navigate]);

  const verifyPayment = async (checkoutId) => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      const response = await fetch(`/api/payments/sumup/verify/${checkoutId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la vérification du paiement');
      }

      const data = await response.json();
      setPaymentDetails(data);

      if (data.is_paid) {
        setStatus('success');
        setMessage('Votre paiement a été effectué avec succès !');
      } else if (data.status === 'PENDING') {
        setStatus('pending');
        setMessage('Votre paiement est en cours de traitement');
      } else {
        setStatus('error');
        setMessage('Le paiement n\'a pas été complété. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      setMessage(error.message || 'Erreur lors de la vérification du paiement');
    }
  };

  const formatAmount = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <VStack spacing={6}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Heading size="md">Vérification en cours...</Heading>
            <Text color="gray.600">
              Veuillez patienter pendant que nous vérifions votre paiement
            </Text>
            <Progress size="sm" isIndeterminate colorScheme="blue" w="100%" />
          </VStack>
        );

      case 'success':
        return (
          <VStack spacing={6}>
            <Icon as={FaCheckCircle} boxSize={16} color="green.500" />
            <Heading size="lg" color="green.600">Paiement réussi !</Heading>
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Cotisation payée</AlertTitle>
                <AlertDescription>
                  {message}
                </AlertDescription>
              </Box>
            </Alert>

            {paymentDetails && (
              <VStack
                spacing={3}
                align="stretch"
                p={4}
                bg="gray.50"
                borderRadius="md"
                w="100%"
              >
                <Heading size="sm" mb={2}>Détails du paiement</Heading>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Montant payé :</Text>
                  <Text fontWeight="bold" color="green.600">
                    {formatAmount(paymentDetails.amount)}
                  </Text>
                </HStack>
                {paymentDetails.transaction_code && (
                  <HStack justify="space-between">
                    <Text fontWeight="medium">Référence :</Text>
                    <Text fontFamily="mono" fontSize="sm">
                      {paymentDetails.transaction_code}
                    </Text>
                  </HStack>
                )}
              </VStack>
            )}

            <Box textAlign="center">
              <Text color="gray.600" mb={2}>
                Un email de confirmation vous a été envoyé.
              </Text>
              <Text fontSize="sm" color="gray.500">
                Redirection automatique dans {countdown} seconde{countdown !== 1 ? 's' : ''}...
              </Text>
            </Box>

            <Button
              colorScheme="green"
              size="lg"
              onClick={() => {
                sessionStorage.removeItem('pending_sumup_payment');
                navigate('/contributions');
              }}
            >
              Retour à mes contributions
            </Button>
          </VStack>
        );

      case 'pending':
        return (
          <VStack spacing={6}>
            <Icon as={FaClock} boxSize={16} color="orange.500" />
            <Heading size="lg" color="orange.600">Paiement en cours</Heading>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Traitement en cours</AlertTitle>
                <AlertDescription>
                  {message}
                </AlertDescription>
              </Box>
            </Alert>

            <Text textAlign="center" color="gray.600">
              Votre paiement est en cours de traitement. Vous recevrez une confirmation
              par email dès que le paiement sera validé.
            </Text>

            <VStack spacing={2}>
              <Button
                colorScheme="blue"
                onClick={() => verifyPayment(searchParams.get('checkout_id'))}
              >
                Vérifier à nouveau
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  sessionStorage.removeItem('pending_sumup_payment');
                  navigate('/contributions');
                }}
              >
                Retour à mes contributions
              </Button>
            </VStack>
          </VStack>
        );

      case 'error':
      default:
        return (
          <VStack spacing={6}>
            <Icon as={FaExclamationTriangle} boxSize={16} color="red.500" />
            <Heading size="lg" color="red.600">Erreur de paiement</Heading>
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Paiement non complété</AlertTitle>
                <AlertDescription>
                  {message}
                </AlertDescription>
              </Box>
            </Alert>

            <Text textAlign="center" color="gray.600">
              Votre paiement n'a pas été traité. Aucun montant n'a été débité de votre compte.
            </Text>

            <VStack spacing={2}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  sessionStorage.removeItem('pending_sumup_payment');
                  navigate('/contributions');
                }}
              >
                Retour à mes contributions
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
              >
                Retour à l'accueil
              </Button>
            </VStack>
          </VStack>
        );
    }
  };

  return (
    <Container maxW="container.md" py={10}>
      <Box
        bg="white"
        p={8}
        borderRadius="lg"
        boxShadow="lg"
        textAlign="center"
      >
        {renderContent()}
      </Box>
    </Container>
  );
}

// Helper Component
function HStack({ children, justify, ...props }) {
  return (
    <Box display="flex" justifyContent={justify} {...props}>
      {children}
    </Box>
  );
}
